<template>
  <div class="card-outline-gate">
    <div class="g-title">{{ payload.reason || '文案你先看看，确认后开始出图' }}</div>
    <div class="g-sub">
      用「{{ styleName(payload.setId) }}」出 {{ payload.totalPages }} 页 ·
      <b>这一步之前改多少次都免费</b>，确认后才扣点
    </div>

    <div class="pages">
      <div v-for="(p, i) in draft" :key="p.index" class="pg">
        <span class="idx">{{ String(p.index).padStart(2, '0') }}</span>

        <!-- ★纯图页：这页版式本来就不渲染文字，给输入框只会让人白填一场 -->
        <div v-if="isPureImage(p)" class="body no-text">
          <i class="el-icon-picture-outline"></i>
          这页是纯图，不放文字（套图里负责"喘口气"）
        </div>

        <!-- 编辑态：逐行直改。走结构化补丁、不过 LLM，比"说一句话让 AI 理解"快十倍也准十倍 -->
        <div v-else-if="editing" class="body">
          <el-input
            v-model="p.headline"
            class="h"
            size="small"
            maxlength="14"
            show-word-limit
            placeholder="标题"
            @input="mark(i)"
          />
          <el-input
            v-model="p.subheadline"
            class="s"
            size="small"
            maxlength="22"
            show-word-limit
            placeholder="副标题"
            @input="mark(i)"
          />
          <div v-if="p.bullets && p.bullets.length" class="bl">
            <span v-for="(b, j) in p.bullets" :key="j" class="b">{{ b.title }}</span>
          </div>
        </div>

        <!-- ★默认只读：这一屏的任务是"读一遍再决定"，摆一堆输入框会让人以为还得填 -->
        <div v-else class="body read" :class="{ changed: dirty[i] }">
          <div class="r-h">{{ p.headline || '—' }}</div>
          <div v-if="p.subheadline" class="r-s">{{ p.subheadline }}</div>
          <div v-if="p.bullets && p.bullets.length" class="bl">
            <span v-for="(b, j) in p.bullets" :key="j" class="b">{{ b.title }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="payload.warnings && payload.warnings.length" class="warn">
      <div v-for="(w, i) in payload.warnings" :key="i">· {{ w }}</div>
    </div>

    <div class="btns">
      <el-button type="primary" size="small" :loading="submitting" @click="confirm">
        确认<template v-if="pointsCost"> · 扣 {{ pointsCost }} 点</template>
      </el-button>
      <el-button v-if="!editing" size="small" :disabled="submitting" @click="editing = true">
        修改
      </el-button>
      <el-button v-else size="small" :disabled="submitting" @click="cancelEdit">
        放弃修改
      </el-button>
      <el-button type="text" size="small" class="quit" :disabled="submitting" @click="$emit('cancel')">
        先不弄了
      </el-button>
      <span v-if="dirtyCount" class="changed-tip">改了 {{ dirtyCount }} 页</span>
    </div>
  </div>
</template>

<script>
import { styleName } from '../constants'

export default {
  name: 'CardOutlineGate',
  props: {
    payload: { type: Object, required: true },
    pointsCost: { type: Number, default: 0 },
    submitting: { type: Boolean, default: false },
  },
  data() {
    return {
      // 深拷一份来改，不动原事件 —— 事件流是历史记录，不该被编辑污染
      draft: JSON.parse(JSON.stringify(this.payload.pages || [])),
      dirty: {},
      editing: false,
    }
  },
  computed: {
    dirtyCount() {
      return Object.keys(this.dirty).length
    },
  },
  methods: {
    styleName,
    /**
     * 这页是不是纯图页（版式本身不渲染文字）。
     * ★textLoad 是后加的字段，早于它挂起的任务快照里没有 —— 那种情况退回看内容：
     *   一个字都没有的页就是纯图页，否则会显示成一行孤零零的"—"。
     */
    isPureImage(p) {
      if (p.textLoad) return p.textLoad === 'none'
      return !p.headline && !p.subheadline && !(p.bullets || []).length
    },
    mark(i) {
      this.$set(this.dirty, i, true)
    },
    cancelEdit() {
      // ★"放弃修改"要真的丢掉改动，不能只是收起输入框、改动还留在 draft 里，
      //   否则用户以为撤销了，一点确认还是把改动发了出去
      this.draft = JSON.parse(JSON.stringify(this.payload.pages || []))
      this.dirty = {}
      this.editing = false
    },
    confirm() {
      // 没改任何东西 → approve；改了 → edit + 只带改动过的页
      const idx = Object.keys(this.dirty).map(Number)
      if (!idx.length) {
        this.$emit('submit', { action: 'approve' })
        return
      }
      const pageEdits = idx.map((i) => ({
        index: this.draft[i].index,
        headline: this.draft[i].headline,
        subheadline: this.draft[i].subheadline,
      }))
      this.$emit('submit', { action: 'edit', pageEdits })
    },
  },
}
</script>

<style lang="scss">
.card-outline-gate {
  min-width: 520px;
  .g-title { font-size: 14px; font-weight: 600; color: #1f2329; }
  .g-sub { margin-top: 4px; font-size: 12px; color: #8a8f98; b { color: #2aa54a; } }

  .pages { margin-top: 12px; }
  .pg { display: flex; padding: 8px 0; border-top: 1px dashed #eceff3; &:first-child { border-top: none; } }
  .idx { width: 28px; padding-top: 8px; color: #b0b4bb; font-size: 12px; flex-shrink: 0; }
  .body { flex: 1; }
  .body.no-text {
    padding: 10px 12px; background: #f6f7f9; border-radius: 6px;
    font-size: 12px; color: #a0a4ab;
    i { margin-right: 5px; }
  }
  .body.read {
    padding: 3px 0 2px;
    .r-h { font-size: 13px; font-weight: 500; color: #1f2329; line-height: 1.6; }
    .r-s { margin-top: 1px; font-size: 12px; color: #8a8f98; line-height: 1.6; }
    /* 改过的页在只读态也要看得出来，否则收起编辑框后不知道自己动过哪几页 */
    &.changed {
      padding-left: 8px;
      border-left: 2px solid #f0a020;
    }
  }
  .h { margin-bottom: 5px; }
  .bl { margin-top: 5px; }
  .b {
    display: inline-block; margin-right: 6px; padding: 2px 8px;
    background: #f0f3f8; border-radius: 10px; font-size: 12px; color: #606266;
  }

  .warn { margin-top: 10px; font-size: 12px; color: #f0a020; line-height: 1.7; }

  .btns { margin-top: 14px; display: flex; align-items: center; gap: 8px; }
  .quit { color: #a0a4ab; &:hover { color: #606266; } }
  .changed-tip { font-size: 12px; color: #f0a020; }
}
</style>
